import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {isPlatformBrowser} from '@angular/common';
import {Observable, of, throwError} from 'rxjs';
import {catchError, finalize, map, shareReplay, tap} from 'rxjs/operators';
import {ApiConstants} from '@coreConstant';

/**
 * How long before the access cookie lapses the interceptor renews it. Covers the request's flight
 * time and a browser clock running a little behind the API's (a token's `exp` is the API's clock).
 */
const RENEW_BEFORE_EXPIRY_MS = 60_000;

/** A JWT's `exp` claim as epoch ms, or null if unreadable. Not verified: it only schedules renewal. */
function jwtExpiry(token: string | null): number | null {
  try {
    const payload = JSON.parse(atob(token!.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload?.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * Holds the access token in memory (never localStorage) so cross-host integrations that do NOT
 * receive the host-only auth cookie — e.g. the Node API v2 (a different host in staging/prod) — can
 * still authenticate with an Authorization: Bearer header. The value is fetched from the cookie-
 * protected /oauth/service-token endpoint on bootstrap and after login.
 *
 * It also tracks when that token — and so the auth cookie carrying it — expires, which is how the
 * interceptor knows to renew the session before a request rather than after a 401.
 */
@Injectable({providedIn: 'root'})
export class ServiceTokenService {

  private token: string | null = null;
  private inFlight$: Observable<string | null> | null = null;
  /** When the access cookie lapses (epoch ms); null when unknown, which disables early renewal. */
  private expiresAt: number | null = null;

  constructor(private http: HttpClient, @Inject(PLATFORM_ID) private platformId: Object) {}

  get(): string | null {
    return this.token;
  }

  clear(): void {
    this.token = null;
    this.expiresAt = null;
  }

  /**
   * A silent refresh replaced the cookies. The held token was minted from the OLD access token, so
   * it is dropped (re-fetched on demand); the new one lives `expiresIn` seconds from now.
   */
  renewed(expiresIn: number | null): void {
    this.token = null;
    this.expiresAt = expiresIn ? Date.now() + expiresIn * 1000 : null;
  }

  /** Whether the access cookie has lapsed or is about to, so the session should be renewed first. */
  isAccessTokenDue(): boolean {
    return this.expiresAt !== null && Date.now() >= this.expiresAt - RENEW_BEFORE_EXPIRY_MS;
  }

  /**
   * Returns a usable service token, fetching it on demand if it isn't in memory yet. Concurrent
   * callers share a single in-flight request (shareReplay), so a burst of cross-host calls right
   * after login never races ahead of the token fetch.
   */
  ensureToken(): Observable<string | null> {
    if (this.token) {
      return of(this.token);
    }
    if (!this.inFlight$) {
      this.inFlight$ = this.refresh().pipe(
        finalize(() => { this.inFlight$ = null; }),
        shareReplay(1)
      );
    }
    return this.inFlight$;
  }

  /** Fetch the token for cross-host calls. Resolves regardless of outcome (guest => null). */
  refresh(): Observable<string | null> {
    if (!isPlatformBrowser(this.platformId)) {
      return of(null);
    }
    return this.http.get(ApiConstants.SERVICE_TOKEN_URL).pipe(
      map((res: any) => (res?.data?.token ?? res?.token ?? null) as string | null),
      tap((token) => {
        this.token = token;
        // After a refresh, `expires_in` (measured on this clock) is already known and wins.
        this.expiresAt ??= jwtExpiry(token);
      }),
      catchError(() => {
        this.token = null;
        return of(null);
      })
    );
  }
}
